import React from 'react';
import { Box, Container, Link as MuiLink, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { AutoAwesome, Check } from '@mui/icons-material';
import {
  landing,
  landingType,
  gradientText,
  float,
  drift,
  reducedMotion,
  focusRingOnDark,
} from '../../pages/landing/landingTokens';
import Wordmark from '../common/Wordmark';

interface AuthSplitLayoutProps {
  eyebrow: string;
  /** Rendered as the brand panel's h2 — the page's h1 belongs to the form side. */
  title: React.ReactNode;
  lead: string;
  points: string[];
  /** The AI line. Kept forward-looking on purpose — nothing here ships yet. */
  aiNote: string;
  children: React.ReactNode;
}

/**
 * Two-up auth shell: brand and argument on the left, form on the right.
 *
 * The left panel is dark and the right is light — the form side stays on a
 * plain surface because legibility of labels, errors and helper text beats
 * atmosphere on a screen whose only job is to be filled in.
 *
 * Below `md` the left panel collapses to a compact header band rather than
 * disappearing, so the brand and the AI line still land on mobile.
 */
const AuthSplitLayout: React.FC<AuthSplitLayoutProps> = ({
  eyebrow,
  title,
  lead,
  points,
  aiNote,
  children,
}) => (
  <Box
    sx={{
      flex: '1 1 auto',
      width: '100%',
      minWidth: 0,
      minHeight: '100vh',
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', md: '1fr 1fr', lg: '1.05fr 1fr' },
      overflowX: 'hidden',
    }}
  >
    {/* ── Brand / argument ── */}
    <Box
      component="aside"
      sx={{
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        px: { xs: 3, sm: 5, lg: 8 },
        py: { xs: 4, md: 6 },
        background: landing.heroGradient,
        backgroundSize: '200% 200%',
        animation: `${drift} 18s linear infinite`,
        ...reducedMotion,
      }}
    >
      {/* Masked grid overlay */}
      <Box
        aria-hidden="true"
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `linear-gradient(${landing.gridLine} 1px, transparent 1px), linear-gradient(90deg, ${landing.gridLine} 1px, transparent 1px)`,
          backgroundSize: '56px 56px',
          maskImage: 'radial-gradient(ellipse 90% 70% at 30% 20%, #000 30%, transparent 100%)',
          WebkitMaskImage: 'radial-gradient(ellipse 90% 70% at 30% 20%, #000 30%, transparent 100%)',
          pointerEvents: 'none',
        }}
      />
      <Box
        aria-hidden="true"
        sx={{
          position: 'absolute',
          top: '-10%',
          right: '-15%',
          width: 460,
          height: 460,
          borderRadius: '50%',
          background: 'rgba(79,133,221,0.16)',
          filter: 'blur(100px)',
          animation: `${float} 14s ease-in-out infinite`,
          pointerEvents: 'none',
          ...reducedMotion,
        }}
      />

      <Box sx={{ position: 'relative', zIndex: 1 }}>
        <MuiLink component={RouterLink} to="/" underline="none" sx={focusRingOnDark}>
          <Wordmark size={32} tone="dark" />
        </MuiLink>
      </Box>

      <Box sx={{ position: 'relative', zIndex: 1, py: { xs: 4, md: 6 }, maxWidth: 520 }}>
        <Typography component="p" sx={{ ...landingType.eyebrow, color: landing.accent, mb: 2 }}>
          {eyebrow}
        </Typography>

        <Typography
          component="h2"
          sx={{
            ...landingType.h2,
            fontSize: { xs: 'clamp(1.75rem, 6vw, 2.25rem)', md: 'clamp(2rem, 3vw, 2.75rem)' },
            color: landing.textOnDark,
          }}
        >
          {title}
        </Typography>

        <Typography
          sx={{
            mt: 2.5,
            fontSize: '1rem',
            lineHeight: 1.7,
            color: landing.textOnDarkMuted,
          }}
        >
          {lead}
        </Typography>

        <Box
          component="ul"
          sx={{
            listStyle: 'none',
            m: 0,
            p: 0,
            mt: 4,
            display: { xs: 'none', sm: 'block' },
          }}
        >
          {points.map((point) => (
            <Box
              component="li"
              key={point}
              sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', mb: 1.75 }}
            >
              <Check sx={{ fontSize: 18, mt: '3px', flexShrink: 0, color: landing.accent }} />
              <Typography sx={{ fontSize: '0.9375rem', lineHeight: 1.6, color: landing.textOnDarkMuted }}>
                {point}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* The AI line, flagged as forward-looking rather than shipped. */}
        <Box
          sx={{
            mt: { xs: 3, md: 5 },
            p: 2.5,
            borderRadius: 2,
            borderLeft: `3px solid ${landing.accent}`,
            backgroundColor: 'rgba(111,163,238,0.06)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <AutoAwesome sx={{ fontSize: 16, color: landing.accent }} />
            <Typography sx={{ ...landingType.eyebrow, color: landing.accent }}>
              The groundwork AI actually needs
            </Typography>
          </Box>
          <Typography sx={{ fontSize: '0.875rem', lineHeight: 1.65, color: landing.textOnDarkMuted }}>
            {aiNote}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ position: 'relative', zIndex: 1, display: { xs: 'none', md: 'block' } }}>
        <Typography sx={{ fontSize: '0.8125rem', color: 'rgba(226,232,240,0.5)' }}>
          © {new Date().getFullYear()} Stratify ·{' '}
          <Box component="span" sx={gradientText}>
            Built for Indian business
          </Box>
        </Typography>
      </Box>
    </Box>

    {/* ── Form ── */}
    <Box
      component="main"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: landing.paper,
        px: { xs: 3, sm: 5 },
        py: { xs: 6, md: 8 },
      }}
    >
      <Container maxWidth="sm" disableGutters sx={{ maxWidth: 460 }}>
        {children}
      </Container>
    </Box>
  </Box>
);

export default AuthSplitLayout;
